// src/components/Cart/CartTable.js
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

const CartTable = ({ 
  cartItems, 
  stockLimits, 
  updateQuantity, 
  removeFromCart, 
  handleDropdownOpen, 
  refreshingStock 
}) => {
  const getStock = (item) => stockLimits[`${item.id}-${item.colorId}-${item.size}`] || 0;

  return (
    <TableContainer 
      sx={{ 
        direction: 'rtl',
        '& .MuiTable-root': {
          direction: 'rtl',
          minWidth: 650,
          borderCollapse: 'separate',
          borderSpacing: 0
        },
        // Enhanced mobile responsiveness
        '@media (max-width: 900px)': {
          '& .MuiTable-root': {
            minWidth: 'auto'
          }
        },
        // Horizontal scroll on very small screens if needed
        overflowX: 'auto'
      }}
    >
      <Table sx={{ direction: 'rtl' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderTopRightRadius: '8px',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              المنتج
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              الوصف
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              السعر
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              الكمية
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderTopLeftRadius: '8px',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              الإجمالي
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cartItems.map((item, idx) => {
            const stock = getStock(item);
            const maxQuantity = Math.min(stock, 10);
            return (
              <TableRow key={`${item.id}-${idx}`} hover>
                <TableCell sx={{
                  textAlign: 'center',
                  direction: 'rtl',
                  padding: '16px 24px',
                  '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                  '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box
                      component="img"
                      src={item.image || '/placeholder.png'}
                      alt={item.name_ar}
                      sx={{ 
                        width: { xs: 50, sm: 60, md: 80 }, 
                        height: { xs: 50, sm: 60, md: 80 }, 
                        objectFit: 'cover', 
                        borderRadius: 1
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{
                  textAlign: 'center',
                  direction: 'rtl',
                  padding: '16px 24px',
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                  '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {item.name_ar}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.5, sm: 1 }, 
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                  }}>
                    <Chip 
                      label={`اللون: ${item.colorName}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        height: { xs: 20, sm: 24 }
                      }}
                    />
                    <Chip 
                      label={`المقاس: ${item.size}`} 
                      size="small" 
                      color="secondary" 
                      variant="outlined"
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        height: { xs: 20, sm: 24 }
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{
                  textAlign: 'center',
                  direction: 'rtl',
                  padding: '16px 24px',
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                  '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {item.price.toFixed(2)} جم
                  </Typography>
                </TableCell>
                <TableCell sx={{
                  textAlign: 'center',
                  direction: 'rtl',
                  padding: '16px 24px',
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                  '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ width: { xs: 60, sm: 70, md: 80 } }}>
                      <InputLabel id={`qty-label-${idx}`} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        الكمية
                      </InputLabel>
                      <Select
                        labelId={`qty-label-${idx}`}
                        value={item.quantity}
                        label="الكمية"
                        onChange={(e) =>
                          updateQuantity(item.id, item.colorId, item.size, e.target.value)
                        }
                        onOpen={() => handleDropdownOpen(item)}
                        disabled={stock < 1 || refreshingStock}
                        sx={{
                          '& .MuiSelect-select': {
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }
                        }}
                      >
                        {stock > 0 ? (
                          Array.from({ length: maxQuantity }, (_, i) => i + 1).map((n) => (
                            <MenuItem key={n} value={n} sx={{ 
                              justifyContent: 'center', 
                              fontWeight: 'bold',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}>
                              {n}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value={1} disabled sx={{ 
                            justifyContent: 'center', 
                            fontWeight: 'bold',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }}>
                            1
                          </MenuItem>
                        )}
                      </Select>
                      {stock < 1 && (
                        <Typography variant="caption" color="error" sx={{ 
                          display: 'block', 
                          fontWeight: 'bold', 
                          textAlign: 'center',
                          fontSize: { xs: '0.65rem', sm: '0.75rem' }
                        }}>
                          غير متوفر
                        </Typography>
                      )}
                      {refreshingStock && (
                        <Typography variant="caption" color="primary" sx={{ 
                          display: 'block', 
                          fontWeight: 'bold', 
                          textAlign: 'center',
                          fontSize: { xs: '0.65rem', sm: '0.75rem' }
                        }}>
                          تحديث...
                        </Typography>
                      )}
                    </FormControl>
                    
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => removeFromCart(item.id, item.colorId, item.size)}
                      sx={{
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        fontWeight: 'bold',
                        minWidth: 'auto',
                        padding: '2px 8px',
                        textTransform: 'none'
                      }}
                    >
                      حذف
                    </Button>
                  </Box>
                </TableCell>
                <TableCell sx={{
                  textAlign: 'center',
                  direction: 'rtl',
                  padding: '16px 24px',
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                  '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {(item.price * item.quantity).toFixed(2)} جم
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CartTable;